import { formatMoneyInput, parseMoneyInput } from './moneyInput';

const K_MULTIPLIER = 1_000n;

/** Format a full game price as the editable number of thousands shown beside the external K suffix. */
export function formatGamePriceK(price: number): string {
  return formatMoneyInput((BigInt(Math.max(0, Math.trunc(price))) / K_MULTIPLIER).toString());
}

/** Parse the editable K-unit field into the full game price without losing large pasted digits. */
export function parseGamePriceK(input: string): bigint {
  return parseMoneyInput(input) * K_MULTIPLIER;
}

/**
 * Clamp a price to its configured range and snap it to the nearest absolute step. Half-step ties
 * round upward, matching the slider's Math.round behavior.
 */
export function normalizeGamePrice(
  price: bigint,
  min: number,
  max: number,
  step: number,
): number {
  const lower = Math.max(0, Math.trunc(min));
  const upper = Math.max(lower, Math.trunc(max));
  const increment = Math.max(1, Math.trunc(step));
  const lowerBig = BigInt(lower);
  const upperBig = BigInt(upper);
  const stepBig = BigInt(increment);

  const clamped = price < lowerBig ? lowerBig : price > upperBig ? upperBig : price;
  const quotient = clamped / stepBig;
  const remainder = clamped % stepBig;
  const snapped = remainder * 2n >= stepBig ? (quotient + 1n) * stepBig : quotient * stepBig;
  const bounded = snapped < lowerBig ? lowerBig : snapped > upperBig ? upperBig : snapped;
  return Number(bounded);
}

/** Move one configured price step while preserving the same normalization and range rules. */
export function stepGamePrice(
  current: number,
  direction: -1 | 1,
  min: number,
  max: number,
  step: number,
): number {
  const increment = Math.max(1, Math.trunc(step));
  return normalizeGamePrice(BigInt(Math.trunc(current)) + BigInt(direction * increment), min, max, increment);
}
